import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, Camera, Loader, Check, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { BRAND, ZORBA, ALL_SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 3;
const MAX_MB = 3;

export default function ShareExperience() {
  const [form, setForm] = useState({
    name: "",
    service: "",
    rating: 5,
    text: "",
    consent: false,
    website: "",
  });
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  function handlePhotos(e) {
    const files = Array.from(e.target.files || []).slice(0, MAX_PHOTOS);
    const tooBig = files.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) {
      setError(`Each photo must be under ${MAX_MB} MB. "${tooBig.name}" is larger — please pick a smaller one.`);
      return;
    }
    setError("");
    setPhotos(files);
    setPreviews(files.map((f) => URL.createObjectURL(f)));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (form.text.trim().length < 20) {
      setError("Please share at least a few sentences so your review helps future visitors.");
      return;
    }
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append("name", form.name.trim());
      body.append("service", form.service || "A Zorba experience");
      body.append("rating", String(form.rating));
      body.append("text", form.text.trim());
      body.append("consent", String(form.consent));
      body.append("website", form.website);
      photos.forEach((p) => body.append("photos", p));
      await axios.post(`${API}/reviews`, body);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Share your experience" },
  ];

  if (done) {
    return (
      <section className="section-pad narrow" data-testid="review-success">
        <div className="success-head">
          <Check size={24} />
          <p className="eyebrow">Received</p>
          <h1>Thank you.</h1>
          <p className="muted">
            Your review has reached our team. We publish every honest review once a quick manual check
            confirms the visit took place. Expect to see it live within a day or two.
          </p>
        </div>
        <div className="actions-row">
          <Link to="/" className="btn btn-primary">Back to home</Link>
          <Link to={`/zorba-international-spa/`} className="btn btn-secondary">See {ZORBA.name}</Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <Seo
        title="Share your experience"
        description="If you've visited Zorba International Spa in Bhubaneswar, we'd love to hear how it went. Share an honest review — a few lines or a longer story, with or without photos."
        path="/share-your-experience/"
      />
      <section className="content-hero section-pad narrow">
        <Breadcrumbs items={crumbs} />
        <p className="eyebrow">{BRAND.short}</p>
        <h1>Share your experience.</h1>
        <p className="large-copy">
          If you've visited {ZORBA.name} in Chandrasekharpur, your words help future guests choose with
          confidence. Keep it honest. Keep it specific. A few sentences is plenty.
        </p>
      </section>

      <form onSubmit={handleSubmit} className="voucher-form review-form section-pad narrow" data-testid="review-form">
        <div className="form-grid">
          <label>
            Your name
            <input
              required
              minLength={2}
              maxLength={80}
              value={form.name}
              onChange={update("name")}
              placeholder="First name is fine"
              data-testid="review-name"
            />
          </label>
          <label>
            Service you tried
            <select value={form.service} onChange={update("service")} data-testid="review-service">
              <option value="">— Choose a service —</option>
              {ALL_SERVICES.map((s) => (
                <option key={s.slug} value={s.name}>{s.name}</option>
              ))}
              <option value="Other / General visit">Other / General visit</option>
            </select>
          </label>
        </div>

        <fieldset className="rating-field">
          <legend>Overall rating</legend>
          <div className="rating-stars" data-testid="review-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                onClick={() => setForm({ ...form, rating: n })}
                className={`star-btn${n <= form.rating ? " active" : ""}`}
                aria-label={`${n} star${n === 1 ? "" : "s"}`}
                data-testid={`rating-star-${n}`}
              >
                <Star size={28} fill={n <= form.rating ? "currentColor" : "none"} />
              </button>
            ))}
          </div>
        </fieldset>

        <label className="full">
          Your review
          <textarea
            required
            rows={6}
            minLength={20}
            maxLength={1500}
            value={form.text}
            onChange={update("text")}
            placeholder="What did the room feel like? How was your therapist? Anything the next guest should know?"
            data-testid="review-text"
          />
          <span className="muted-small">{form.text.length} / 1500 characters</span>
        </label>

        <label className="photo-upload">
          <span>Add photos (optional, up to {MAX_PHOTOS}, {MAX_MB} MB each)</span>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            onChange={handlePhotos}
            data-testid="review-photos"
          />
          <span className="photo-upload-cta">
            <Camera size={14} /> Choose files
          </span>
        </label>
        {previews.length > 0 && (
          <div className="photo-previews" data-testid="review-photo-previews">
            {previews.map((src, i) => (
              <img key={i} src={src} alt={`preview-${i}`} />
            ))}
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={update("consent")}
            required
            data-testid="review-consent"
          />
          <span>
            I agree to {BRAND.short} publishing this review with my first name and any uploaded photos.
            I understand reviews are moderated for honesty — nothing fabricated.
          </span>
        </label>

        <input
          type="text"
          name="website"
          value={form.website}
          onChange={update("website")}
          tabIndex={-1}
          autoComplete="off"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          aria-hidden="true"
        />

        {error && <p className="form-error" data-testid="review-error">{error}</p>}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
          data-testid="review-submit"
        >
          {submitting ? (
            <>
              <Loader size={14} className="spin" /> Sending…
            </>
          ) : (
            "Submit review"
          )}
        </button>
        <p className="muted-small flex-mini">
          <ShieldCheck size={13} /> We only publish authentic reviews from real visits. Our team reviews each submission.
        </p>
      </form>
    </>
  );
}
